import { useState, ChangeEvent, useImperativeHandle, useRef } from 'react';
import { Button, Popover, PopoverContent, PopoverTrigger } from "@heroui/react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRemove, faUpload } from '@fortawesome/free-solid-svg-icons';

interface IFileUploadProps {
    multiple?: boolean;
    acceptedExtensions?: string[];
    maxFiles?: number;
    onFilesSelected(files: FileList | null): void;
}

const FileUpload = (
    {
        ref,
        multiple = false,
        acceptedExtensions = Array.from('*'),
        maxFiles = 1,
        onFilesSelected
    }: IFileUploadProps & {
        ref: React.RefObject<unknown>;
    }
) => {

    const [fileNames, setFileNames] = useState<string[]>();
    const [error, setError] = useState<string>('');

    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;

        if (!files || files.length === 0) {
            return;
        }

        const invalidFiles = [];
        for (const file of files) {
            const fileExtension = file.name.match(/\.[^.]+$/)?.[0];
            if (fileExtension && !acceptedExtensions.includes(fileExtension)) {
                invalidFiles.push(file);
            }
        }

        if (invalidFiles.length > 0) {
            setError(`You can only upload files with the following extensions: ${acceptedExtensions.join(', ')}.`);
            return;
        }


        if (multiple && files && files.length > maxFiles) {
            event.target.files = null;
            setError(`You can only upload a maximum of ${maxFiles} files.`);
            return;
        }

        setFileNames(files ? Array.from(files).map(file => file.name) : []);
        onFilesSelected(files);
    };

    // Expose methods via the ref
    useImperativeHandle(ref, () => ({
        clearFiles: () => clearFiles()
    }));

    const clearFiles = () => {
        if (fileInputRef.current) {
            fileInputRef.current.value = ""; // Clear the file input
            fileInputRef.current.files = null;
        }
        setFileNames([]);
    };

    return (<div className='flex flex-col'>
        <div className='flex items-center'>
            <input
                ref={fileInputRef}
                name='contact-us-file-upload'
                type="file"
                accept={acceptedExtensions.join(', ')}
                multiple={multiple}
                onChange={handleFileChange}
                id="file-upload"
                className="hidden"
            />
            <label htmlFor="file-upload">
                <Button as="span" size="sm" className="bg-msi-chart-orange text-white rounded-l rounded-r-none" startContent={<FontAwesomeIcon icon={faUpload} />}
                    onPress={() => setError('')}>
                    {multiple ? 'Choose Files' : 'Choose File'}
                </Button>
            </label>

            <Popover size="sm" placement="top" showArrow offset={5}
                classNames={{
                    content: 'rounded-sm'
                }}>
                <PopoverTrigger >
                    <Button className={`${(!multiple || (multiple && (!fileNames || fileNames?.length === 0))) ? 'pointer-events-none' : ''}
                         w-full max-w-full border-1 text-black border-msi-chart-orange rounded-none border-x-0`}
                        disableRipple={true}
                        variant='bordered' size='sm' color="primary" >
                        {multiple && fileNames?.length && fileNames?.length >= 1 ? `${fileNames?.length} files chosen` : fileNames?.[0] || 'No file chosen'}
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[240px]">
                    {(titleProps) => (
                        <div className="px-1 py-2 w-full">
                            <p className="text-small font-bold text-foreground" {...titleProps}>
                                Files
                            </p>
                            <div className="mt-2 flex flex-col gap-1 w-full">
                                {fileNames?.map((fileName, index) => (
                                    <p key={index} className="text-small text-foreground">
                                        {fileName}
                                    </p>
                                ))}
                            </div>
                        </div>
                    )}
                </PopoverContent>
            </Popover>
            <Button as="button" isDisabled={!fileNames || fileNames?.length === 0} size="sm" className="bg-msi-chart-orange text-white rounded-r rounded-l-none min-w-0"
                onPress={clearFiles} >
                <FontAwesomeIcon className='px-0' icon={faRemove} />
            </Button>
        </div>
        {error && (
            <div>
                <div className='text-xs text-red-500'>{error}</div>
            </div>
        )}
    </div>)

};

export default FileUpload;

